import { ArrowDownIcon, Button, CloseIcon, ErrorIcon, Modal, SpinnerWithBackground, Text } from '@viapeople/components'
import React from 'react'

interface ModalLoadingStateProps {
	title?: string
	onClose: () => void
}

export const ModalLoadingState = ({ title = 'Loading...', onClose }: ModalLoadingStateProps) => {
	return (
		<Modal isOpen={true} onClose={onClose} fullContent={true} fullScreen={true} title={title} bodyClassName='h-full'>
			<div className='flex items-center justify-center h-full'>
				<SpinnerWithBackground />
			</div>
		</Modal>
	)
}

interface ModalErrorStateProps {
	title?: string
	errorMessage?: string
	errorDescription?: string
	technicalDetails?: string
	logoUrl?: string
	onClose: () => void
	onRetry?: () => void
}

export const ModalErrorState = ({
	title = 'Error',
	errorMessage = 'Unable to load',
	errorDescription = 'Something went wrong loading this page. Please try again or contact support if the issue persists.',
	technicalDetails,
	logoUrl,
	onClose,
	onRetry,
}: ModalErrorStateProps) => {
	const [showDetails, setShowDetails] = React.useState(false)
	const [logoLoadError, setLogoLoadError] = React.useState(false)

	return (
		<Modal
			isOpen={true}
			onClose={onClose}
			fullContent={true}
			fullScreen={true}
			hideHeader={true}
			title={title}
			bodyClassName='h-full'>
			<div className='flex flex-col h-full'>
				<div className='flex items-center justify-between px-6 py-4 border-b border-neutral-100 shrink-0'>
					<div>
						{logoUrl && !logoLoadError && (
							<img
								src={logoUrl}
								alt='Organization logo'
								className='h-8 object-contain'
								onError={() => setLogoLoadError(true)}
							/>
						)}
					</div>
					<button
						type='button'
						onClick={onClose}
						className='text-neutral-500 hover:text-neutral-700 transition-colors p-1 rounded-full hover:bg-neutral-100 cursor-pointer'
						aria-label='Close'>
						<CloseIcon />
					</button>
				</div>

				<div className='flex flex-1 items-center justify-center p-8'>
					<div className='flex flex-col items-center gap-8 w-full max-w-sm'>
						<div className='flex items-center justify-center w-20 h-20 rounded-full bg-error-50 border border-error-100'>
							<ErrorIcon size={40} className='text-error-600' />
						</div>

						<div className='flex flex-col items-center gap-2 text-center'>
							<Text variant='h5' weight='semibold' className='text-neutral-900'>
								{errorMessage}
							</Text>
							<Text variant='small' className='text-neutral-500'>
								{errorDescription}
							</Text>
						</div>

						<div className='flex flex-col items-center gap-4 w-full'>
							<div className='flex items-center gap-3'>
								<Button variant='secondary' onClick={onClose}>
									Go Back
								</Button>
								{onRetry && (
									<Button variant='primary' onClick={onRetry}>
										Try Again
									</Button>
								)}
							</div>

							{technicalDetails && (
								<div className='flex flex-col items-center gap-2 w-full'>
									<button
										type='button'
										onClick={() => setShowDetails((v) => !v)}
										aria-expanded={showDetails}
										className='flex items-center gap-1.5 px-3 py-2 border border-neutral-200 rounded-lg text-xs text-neutral-500 hover:text-neutral-700 hover:border-neutral-300 hover:bg-neutral-50 transition-colors'>
										<span>{showDetails ? 'Hide technical details' : 'Show technical details'}</span>
										<span
											style={{
												transform: showDetails ? 'rotate(180deg)' : 'rotate(0deg)',
												transition: 'transform 0.2s',
											}}>
											<ArrowDownIcon size={12} className='text-neutral-400' />
										</span>
									</button>
									{showDetails && (
										<pre className='w-full bg-neutral-50 border border-neutral-200 rounded-lg p-3 text-xs font-mono text-neutral-600 whitespace-pre-wrap break-all overflow-auto max-h-40'>
											{technicalDetails}
										</pre>
									)}
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
		</Modal>
	)
}
